import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';

function KPI({ label, value, sub }) {
  return (
    <div className="rounded-thin border border-surface-border dark:border-surface-dark-border p-3 bg-surface-panel dark:bg-surface-dark-panel">
      <div className="text-xs uppercase text-surface-muted tracking-wider">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-surface-muted mt-1">{sub}</div>}
    </div>
  );
}

export default function StorageArchive() {
  const [range, setRange, apiParams] = useRangeState('last_30');
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'storage_archive', apiParams],
    queryFn: () => reportsApi.storageAndArchive(apiParams),
    keepPreviousData: true,
  });

  const per = data?.per_tenant_month || [];
  const tiers = data?.per_tier || [];
  const totals = data?.totals || {};

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Storage & archive</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="storage-and-archive" params={apiParams} disabled={isLoading} />
          <button onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Voice GB total"   value={totals.voice_gb ?? '—'}  sub={`${totals.voice_count ?? 0} recordings`} />
        <KPI label="Screen GB total"  value={totals.screen_gb ?? '—'} sub={`${totals.screen_count ?? 0} recordings`} />
        <KPI label="Tenants tracked"  value={new Set(per.map(r => r.tenant)).size || '—'} />
        <KPI label="Months in range"  value={new Set(per.map(r => r.month)).size || '—'} />
      </div>

      <h3 className="text-sm font-semibold mb-2">Per tenant × month</h3>
      <ReportTable
        headers={['tenant','month','voice_count','voice_gb','screen_count','screen_gb']}
        rows={per}
        columnLabels={{
          tenant: 'Tenant', month: 'Month',
          voice_count: 'Voice #', voice_gb: 'Voice GB',
          screen_count: 'Screen #', screen_gb: 'Screen GB',
        }}
      />

      <h3 className="text-sm font-semibold mt-4 mb-2">Voice by storage tier</h3>
      <ReportTable
        headers={['tier','voice_count','voice_gb']}
        rows={tiers}
        columnLabels={{ tier: 'Tier', voice_count: 'Count', voice_gb: 'GB' }}
      />
    </div>
  );
}
